/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';


import MuiTextField, { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField';


import MaskedInput, { MaskedInputProps } from 'react-text-mask';
/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */


import { FieldRenderProps } from 'react-final-form';

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

export const UK_DATE = {
    mask: [/[0-3U]/, /[0-9K]/, '/', /[0-1U]/, /[0-9K]/, '/', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/],
    showMask: true
};

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

type TextFieldsProps = Partial<Omit<MuiTextFieldProps, 'type'>> & FieldRenderProps<any, any> & { maskedFormat: MaskedInputProps; }

interface IMaskedField extends TextFieldsProps {
}

interface IInternalMaskedFormatProps {
    inputRef: (instance: MaskedInput | null) => void;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */
const InternalMaskedFormat: React.FunctionComponent<IInternalMaskedFormatProps> = ({
    inputRef,
    ...rest
}) => {

    return (
        <MaskedInput
            {...rest}
            ref={inputRef}
        />
    );
}


const MaskedField: React.FunctionComponent<IMaskedField> = ({
    input: { name, value, onChange: onInputChange, checked, ...restInput },
    meta,
    fullWidth,
    onChange,
    InputProps,
    maskedFormat,
    ...rest
}) => {

    const onChangeCombined = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (onChange) {
            onChange(event);
        }

        onInputChange(event);
    }, [onChange, onInputChange]);

    return (
        <MuiTextField
            {...rest as any}
            fullWidth={fullWidth === undefined ? true : fullWidth}
            InputProps={{
                ...InputProps,
                inputComponent: InputProps?.inputComponent === undefined ? InternalMaskedFormat : InputProps?.inputComponent
            }}
            inputProps={{ ...restInput, ...maskedFormat }}
            name={name}
            value={value}
            onChange={onChangeCombined}
        />
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default MaskedField;
